import { StandardEvents } from '@shopify/events';

class DhStoreStock extends HTMLElement {
  #status;
  #locations;
  #template;
  #productTitle;
  #variant;
  #sku;

  #labels = {};
  #phones = {};

  #abortController;
  #loadedVariantId = null;
  #section;

  connectedCallback() {
    this.#status = this.querySelector(
      '[data-dh-store-stock-status]',
    );

    this.#locations = this.querySelector(
      '[data-dh-store-stock-locations]',
    );

    this.#template = this.querySelector(
      '[data-dh-store-stock-location-template]',
    );

    this.#productTitle = this.querySelector(
      '[data-dh-store-stock-product-title]',
    );

    this.#variant = this.querySelector(
      '[data-dh-store-stock-variant]',
    );

    this.#sku = this.querySelector(
      '[data-dh-store-stock-sku]',
    );

    const configNode = this.querySelector(
      '[data-dh-store-stock-config]',
    );

    if (configNode?.textContent) {
      try {
        const config = JSON.parse(
          configNode.textContent,
        );

        this.#labels =
          config.labels || {};

        this.#phones =
          config.phones || {};
      } catch (error) {
        console.warn(
          '[DH Store Stock] Unable to read configuration:',
          error,
        );

        this.#labels = {};
        this.#phones = {};
      }
    }

    this.#section = this.closest(
      '.shopify-section, dialog',
    );

    this.#section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
    );

    const variantId =
      this.dataset.variantId;

    if (!variantId) {
      this.#renderError();
      return;
    }

    /*
     * Horizon now controls the accordion.
     *
     * Load store availability as soon as
     * this custom element connects.
     */
    this.#load(variantId);
  }

  disconnectedCallback() {
    this.#abortController?.abort();

    this.#section?.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
    );
  }

  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      ?.then(({ detail }) => {
        const variant =
          detail?.resource;

        if (!variant?.id) {
          return;
        }

        const nextVariantId =
          String(variant.id);

        if (
          nextVariantId ===
          this.dataset.variantId
        ) {
          return;
        }

        this.dataset.variantId =
          nextVariantId;

        this.#loadedVariantId = null;

        this.#abortController?.abort();

        this.#reset();

        this.#updateVariantDetails({
          title: variant.title,
          sku: variant.sku,
        });

        this.#load(nextVariantId);
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[DH Store Stock] Variant event failed:',
            error,
          );
        }
      });
  };

  async #load(variantId) {
    if (!variantId) {
      this.#renderError();
      return;
    }

    if (
      this.#loadedVariantId ===
      variantId
    ) {
      return;
    }

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    this.#reset();

    this.#setStatus(
      this.#labels.loading ||
        'Checking store availability…',
    );

    try {
      const endpoint =
        this.dataset.endpoint ||
        '/apps/dh-store-stock';

      const url = new URL(
        endpoint,
        window.location.origin,
      );

      /*
       * Only send the selected variant ID.
       *
       * Do not append Horizon section
       * parameters such as section_id.
       */
      url.searchParams.set(
        'variant_id',
        variantId,
      );

      const response = await fetch(
        url.toString(),
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json',
          },

          credentials:
            'same-origin',

          signal:
            this.#abortController
              .signal,
        },
      );

      if (!response.ok) {
        throw new Error(
          `Store stock request failed: ${response.status}`,
        );
      }

      const payload =
        await response.json();

      if (!payload?.ok) {
        throw new Error(
          payload?.error ||
            'Store stock request failed',
        );
      }

      const stores =
        Array.isArray(
          payload.stores,
        )
          ? payload.stores
          : [];

      this.#loadedVariantId =
        variantId;

      this.#updateProductDetails(
        payload.variant,
      );

      this.#renderLocations(
        stores,
      );
    } catch (error) {
      if (
        error?.name ===
        'AbortError'
      ) {
        return;
      }

      console.warn(
        '[DH Store Stock] Request failed:',
        error,
      );

      this.#renderError();
    }
  }

  #updateProductDetails(
    variant,
  ) {
    if (!variant) {
      return;
    }

    if (
      this.#productTitle &&
      variant.product_title
    ) {
      this.#productTitle
        .textContent =
        variant.product_title;
    }

    this.#updateVariantDetails({
      title: variant.title,
      sku: variant.sku,
    });
  }

  #updateVariantDetails({
    title,
    sku,
  }) {
    if (this.#variant) {
      const cleanTitle =
        title &&
        title !==
          'Default Title'
          ? title
          : '';

      this.#variant.textContent =
        cleanTitle;

      this.#variant.hidden =
        !cleanTitle;
    }

    if (this.#sku) {
      const cleanSku = sku
        ? String(sku).trim()
        : '';

      this.#sku.textContent =
        cleanSku
          ? `SKU: ${cleanSku}`
          : '';

      this.#sku.hidden =
        !cleanSku;
    }
  }

  #renderLocations(
    locations,
  ) {
    if (
      !this.#locations ||
      !(
        this.#template instanceof
        HTMLTemplateElement
      )
    ) {
      return;
    }

    this.#locations
      .replaceChildren();

    const showOutOfStock =
      this.dataset
        .showOutOfStock ===
      'true';

    const visibleLocations =
      showOutOfStock
        ? locations
        : locations.filter(
            (location) =>
              location.status !==
              'out_of_stock',
          );

    if (
      visibleLocations.length ===
      0
    ) {
      this.#locations.hidden =
        true;

      this.#setStatus(
        this.#labels.none ||
          'This item is currently unavailable in our stores.',
      );

      return;
    }

    const fragment =
      document.createDocumentFragment();

    for (
      const location of
      visibleLocations
    ) {
      const node =
        this.#template.content
          .firstElementChild
          ?.cloneNode(true);

      if (
        !(
          node instanceof
          HTMLElement
        )
      ) {
        continue;
      }

      const dot =
        node.querySelector(
          '[data-status]',
        );

      const name =
        node.querySelector(
          '[data-name]',
        );

      const phone =
        node.querySelector(
          '[data-phone]',
        );

      const phoneSeparator =
        node.querySelector(
          '[data-phone-separator]',
        );

      const label =
        node.querySelector(
          '[data-label]',
        );

      const status = [
        'in_stock',
        'low_stock',
        'out_of_stock',
      ].includes(
        location.status,
      )
        ? location.status
        : 'out_of_stock';

      if (
        dot instanceof
        HTMLElement
      ) {
        dot.dataset.status =
          status;
      }

      if (name) {
        name.textContent =
          location.name || '';
      }

      /*
       * Phone number itself is the
       * clickable telephone link.
       *
       * Example display:
       *
       * Burnside · (08) 8355 5266
       */
      if (
        phone instanceof
        HTMLAnchorElement
      ) {
        const phoneNumber =
          this.#phones[
            location.name
          ] || '';

        if (phoneNumber) {
          phone.textContent =
            phoneNumber;

          phone.href =
            `tel:${this.#phoneHref(
              phoneNumber,
            )}`;

          phone.hidden =
            false;

          if (
            phoneSeparator instanceof
            HTMLElement
          ) {
            phoneSeparator.hidden =
              false;
          }
        } else {
          phone.textContent = '';

          phone.removeAttribute(
            'href',
          );

          phone.hidden = true;

          if (
            phoneSeparator instanceof
            HTMLElement
          ) {
            phoneSeparator.hidden =
              true;
          }
        }
      }

      if (label) {
        label.textContent =
          this.#labels[
            status
          ] ||
          status.replaceAll(
            '_',
            ' ',
          );
      }

      fragment.appendChild(
        node,
      );
    }

    this.#locations
      .appendChild(fragment);

    this.#locations.hidden =
      false;

    this.#setStatus('');
  }

  #phoneHref(
    phoneNumber,
  ) {
    return String(
      phoneNumber,
    )
      .trim()
      .replace(
        /[^\d+]/g,
        '',
      );
  }

  #renderError() {
    if (this.#locations) {
      this.#locations
        .replaceChildren();

      this.#locations.hidden =
        true;
    }

    this.#setStatus(
      this.#labels.error ||
        'Store availability is temporarily unavailable. Please try again.',
    );
  }

  #reset() {
    if (this.#locations) {
      this.#locations
        .replaceChildren();

      this.#locations.hidden =
        true;
    }

    this.#setStatus('');
  }

  #setStatus(message) {
    if (!this.#status) {
      return;
    }

    this.#status.textContent =
      message;

    this.#status.hidden =
      !message;
  }
}

if (
  !customElements.get(
    'dh-store-stock',
  )
) {
  customElements.define(
    'dh-store-stock',
    DhStoreStock,
  );
}